import { useMutation } from '@apollo/client';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import UPDATE_WAREHOUSE_RELEASE_RECEIVER from 'api/mutations/UPDATE_WAREHOUSE_RELEASE_RECEIVER';
import UPDATE_WAREHOUSE_RELEASE_REMARKS from 'api/mutations/UPDATE_WAREHOUSE_RELEASE_REMARKS';
import useDebounceMutation from 'hooks/useDebounceUpdate';
import Button from 'layout/atoms/Button/Button';
import Input from 'layout/atoms/Input/Input';
import InputLabel from 'layout/atoms/InputLabel';
import InputNumber from 'layout/atoms/InputNumber';
import Loader from 'layout/atoms/Loader/Loader';
import Switch from 'layout/atoms/Switch/Switch';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid/TableGrid';
import Textarea from 'layout/atoms/Textarea/Textarea';
import Grid from 'layout/molecules/Grid/Grid';
import SpecificationCard from 'layout/molecules/SpecificationCard';
import { openModal } from 'store/layoutSlice';
import { removeWarehouseReleaseModel, selectWarehouseReleaseDetailsItem, selectWarehouseReleaseDetailsStatus, selectWarehouseReleaseModel, setWarehouseReleaseModelToNotReady, setWarehouseReleaseModelToReady, updateWarehouseReleaseModelVariant, updateWarehouseReleaseModelVariantStore, updateWarehouseReleaseSummary, } from 'store/warehouseReleaseDetailsSlice';
function Summary({ match }) {
    var _a, _b;
    const { t } = useTranslation(['warehouseReleases', 'common']);
    const dispatch = useDispatch();
    const [debounceUpdate, saving] = useDebounceMutation();
    const status = useSelector(selectWarehouseReleaseDetailsStatus);
    const warehouseReleaseDetails = useSelector(selectWarehouseReleaseDetailsItem);
    const warehouseReleaseModel = useSelector(selectWarehouseReleaseModel);
    const variants = (_a = warehouseReleaseModel === null || warehouseReleaseModel === void 0 ? void 0 : warehouseReleaseModel.variants) === null || _a === void 0 ? void 0 : _a.edges.map((variant) => variant.node);
    const isDraft = (warehouseReleaseDetails === null || warehouseReleaseDetails === void 0 ? void 0 : warehouseReleaseDetails.status) === 'DRAFT';
    const isSetToReadyAvailable = warehouseReleaseModel === null || warehouseReleaseModel === void 0 ? void 0 : warehouseReleaseModel.isSetToReadyAvailable;
    const [updateReceiver] = useMutation(UPDATE_WAREHOUSE_RELEASE_RECEIVER);
    const [updateRemarks] = useMutation(UPDATE_WAREHOUSE_RELEASE_REMARKS);
    const notEditable = ['APPROVED', 'DONE'].includes(warehouseReleaseDetails.status);
    const sizes = Array.from(new Set(variants === null || variants === void 0 ? void 0 : variants.map((v) => v.warehouseReleaseModelSize.name)));
    const colors = Array.from(new Set(variants === null || variants === void 0 ? void 0 : variants.map((v) => v.warehouseReleaseModelColor.name)));
    const columns = [['120px', '1fr'], ...sizes.map(() => ['100px', '1fr'])];
    const handleReceiverChange = useCallback((e) => {
        const { name, value } = e.target;
        dispatch(updateWarehouseReleaseSummary({ name, value }));
        debounceUpdate(updateReceiver, {
            warehouseReleaseNodeId: warehouseReleaseDetails.id,
            [name]: value,
        }, undefined);
    }, [dispatch, debounceUpdate, updateReceiver, warehouseReleaseDetails.id]);
    const handleRemarksChange = useCallback((e) => {
        const { name, value } = e.target;
        dispatch(updateWarehouseReleaseSummary({ name, value }));
        debounceUpdate(updateRemarks, {
            warehouseReleaseNodeId: warehouseReleaseDetails.id,
            [name]: value,
        }, undefined);
    }, [dispatch, debounceUpdate, updateRemarks, warehouseReleaseDetails.id]);
    // const debouncedUpdateModelVariants = useRef(
    //   debounce((dispatch, name, value) => {
    //     dispatch(
    //       setOrderComponentVariantConsumptionOverride({ orderComponentVariantId: name, value }),
    //     );
    //   }, 1000),
    // ).current;
    // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //   const { name, value } = e.target;
    //   // dispatch(setOrderComponentVariantConsumptionOverride({ orderComponentVariantId: name, value }));
    //   debouncedUpdateModelVariants.cancel();
    //   debouncedUpdateModelVariants(dispatch, name, value);
    // };
    const debouncedUpdateModelVariants = useRef(debounce((dispatch, name, value) => {
        dispatch(updateWarehouseReleaseModelVariant({ variantId: name, value }));
    }, 1000)).current;
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        dispatch(updateWarehouseReleaseModelVariantStore({ variantId: name, value }));
        debouncedUpdateModelVariants.cancel();
        debouncedUpdateModelVariants(dispatch, name, value);
    };
    const handleSwitchChange = (value) => {
        if (value) {
            dispatch(setWarehouseReleaseModelToNotReady(warehouseReleaseModel.id));
        }
        else {
            dispatch(setWarehouseReleaseModelToReady(warehouseReleaseModel.id));
        }
    };
    if (status.fetch === 'idle' || status.fetch === 'loading') {
        return React.createElement(Loader, null);
    }
    if (status.fetch === 'failed') {
        return React.createElement("p", null, "error");
    }
    return (React.createElement(Grid, { gap: 24 },
        React.createElement(Grid, { columns: "aside_left", gap: 0 },
            warehouseReleaseModel || status.fetchModel === 'loading' ? (React.createElement(Grid, { gap: 12 },
                status.fetchModel === 'loading' || status.fetchModel === 'idle' ? (React.createElement(Loader, null)) : ((warehouseReleaseDetails === null || warehouseReleaseDetails === void 0 ? void 0 : warehouseReleaseDetails.warehouseReleaseModel) && (React.createElement(SpecificationCard, { layout: "grid", specification: (_b = warehouseReleaseDetails === null || warehouseReleaseDetails === void 0 ? void 0 : warehouseReleaseDetails.warehouseReleaseModel) === null || _b === void 0 ? void 0 : _b.modelSpecification }))),
                warehouseReleaseModel && (React.createElement(React.Fragment, null,
                    React.createElement(Button, { color: "primary", to: `${match.url}/specifications`, onClick: () => {
                            dispatch(openModal({ name: 'warehouseReleaceSpecificationPicker' }));
                        } }, t('common:change')),
                    React.createElement(Button, { loading: status.remove === 'loading', onClick: () => dispatch(removeWarehouseReleaseModel({
                            warehouseReleaseModelId: warehouseReleaseDetails === null || warehouseReleaseDetails === void 0 ? void 0 : warehouseReleaseDetails.warehouseReleaseModel.id,
                        })) }, t('clear')))))) : (React.createElement(Grid, { gap: 12 },
                React.createElement(Button, { color: "primary", to: `${match.url}/specifications`, onClick: () => {
                        dispatch(openModal({ name: 'warehouseReleaceSpecificationPicker' }));
                    } }, t('common:add')))),
            React.createElement("div", { style: { padding: '0 24px' } },
                React.createElement(Grid, null,
                    React.createElement("div", null,
                        React.createElement(InputLabel, { text: t('receiver') }),
                        React.createElement(Input, { name: "receiver", value: (warehouseReleaseDetails === null || warehouseReleaseDetails === void 0 ? void 0 : warehouseReleaseDetails.receiver) || '', onChange: handleReceiverChange, loading: saving, disabled: notEditable })),
                    React.createElement("div", null,
                        React.createElement(InputLabel, { text: t('common:created_date') }),
                        React.createElement(Input, { name: "createdDate", value: moment(warehouseReleaseDetails === null || warehouseReleaseDetails === void 0 ? void 0 : warehouseReleaseDetails.createdDate).format('YYYY-MM-DD HH:mm') || '', onChange: () => { }, disabled: true })),
                    React.createElement("div", null,
                        React.createElement(InputLabel, { text: t('common:modified_date') }),
                        React.createElement(Input, { name: "modifiedDate", value: moment(warehouseReleaseDetails === null || warehouseReleaseDetails === void 0 ? void 0 : warehouseReleaseDetails.modifiedDate).format('YYYY-MM-DD HH:mm') || '', onChange: () => { }, disabled: true })),
                    React.createElement("div", null,
                        React.createElement(InputLabel, { text: t('notes') }),
                        React.createElement(Textarea, { name: "remarks", value: (warehouseReleaseDetails === null || warehouseReleaseDetails === void 0 ? void 0 : warehouseReleaseDetails.remarks) || '', onChange: handleRemarksChange, loading: saving, rows: 5, disabled: notEditable }))))),
        warehouseReleaseModel && (React.createElement(Grid, null,
            React.createElement("h3", null, t('warehouse_release_volume_title')),
            React.createElement(TableGrid, { columns: columns },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        React.createElement(Cell, null, t('version')),
                        sizes.map((size) => (React.createElement(Cell, { key: size }, size))))),
                React.createElement(Body, null, colors.map((color) => (React.createElement(Row, { key: color },
                    React.createElement(Cell, null, color),
                    sizes.map((size) => {
                        const variant = variants.find((v) => v.warehouseReleaseModelSize.name === size &&
                            v.warehouseReleaseModelColor.name === color);
                        return (React.createElement(Cell, { key: size }, variant ? (React.createElement(InputNumber, { name: variant.id, value: variant.quantity, maxValue: variant.correspondingSpecificationVariant.quantity, onChange: handleInputChange, suffix: `/ ${variant.correspondingSpecificationVariant.quantity}`, disabled: warehouseReleaseModel === null || warehouseReleaseModel === void 0 ? void 0 : warehouseReleaseModel.ready })) : ('')));
                    })))))))),
        isDraft && (React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' } },
            React.createElement("h3", null, t('common:done')),
            React.createElement(Switch, { value: warehouseReleaseModel === null || warehouseReleaseModel === void 0 ? void 0 : warehouseReleaseModel.ready, disabled: !(warehouseReleaseModel === null || warehouseReleaseModel === void 0 ? void 0 : warehouseReleaseModel.ready) && !isSetToReadyAvailable, onChange: () => handleSwitchChange(warehouseReleaseModel.ready) })))));
}
export default memo(withRouter(Summary));
